import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { EffectCards } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/effect-cards';
import { TutorCard } from '../components/TutorCard';
import AvailabilityLegend from '../components/AvailabilityLegend';
import { RatingModal } from '../components/RatingModal';
import { ContactRequestModal } from '../components/ContactRequestModal';
import { ContactInfoModal } from '../components/ContactInfoModal';
import { useTutors } from '../hooks/useTutors';
import { Profile } from '../types';
import { useFilterOptions } from '../hooks/useFilterOptions';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { BackButton } from '../components/BackButton';

interface TutorsPageProps {
  showToast: (message: string, type?: 'success' | 'error') => void;
}

export const TutorsPage: React.FC<TutorsPageProps> = ({ showToast }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Récupérer les filtres avancés depuis FilterPage si disponibles
  const advancedFilters = location.state?.filters;
  
  const { tutors, loading, error, connectionStatus, refreshTutors, searchTutors } = useTutors({ 
    filters: advancedFilters,
    autoLoad: !advancedFilters // Ne charge automatiquement que si pas de filtres avancés
  });
  const { options: filterOptions, loading: optionsLoading } = useFilterOptions();
  const [selectedFiliere, setSelectedFiliere] = useState<string>('tous');

  // Charger les tuteur-trices avec les filtres avancés si présents
  useEffect(() => {
    if (advancedFilters) {
      searchTutors(advancedFilters);
      // Scroll vers le haut de la page après application des filtres
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [advancedFilters, searchTutors]);

  // Filtrer les tuteur-trices selon la filière sélectionnée
  const filteredTutors = selectedFiliere === 'tous' 
    ? tutors 
    : tutors.filter(tutor => tutor.filiere === selectedFiliere);

  // SmartMatch HEG toujours activé
  const smartMatch = true;
  const displayTutors = useMemo(() => {
    if (!smartMatch) return filteredTutors;
    if (!filteredTutors || filteredTutors.length === 0) return filteredTutors;
    const { sortTutorsSmartMatch } = require('../utils/smartMatch');
    return sortTutorsSmartMatch(filteredTutors);
  }, [filteredTutors, smartMatch]);

  // États pour les modales
  const [selectedTutor, setSelectedTutor] = useState<Profile | null>(null);
  const [isRatingModalOpen, setIsRatingModalOpen] = useState(false);
  const [contactModalData, setContactModalData] = useState<{
    isOpen: boolean;
    tutorName: string;
    isSuccess: boolean;
    message?: string;
  }>({ isOpen: false, tutorName: '', isSuccess: false });

  const [contactInfoModalData, setContactInfoModalData] = useState<{
    isOpen: boolean;
    tutorUserId: string;
  }>({ isOpen: false, tutorUserId: '' });

  const handleRatingSuccess = async () => {
    try {
      await refreshTutors();
      setIsRatingModalOpen(false);
      setSelectedTutor(null);
    } catch (error) {
    }
  };

  const openRatingModal = (tutor: Profile) => {
    setSelectedTutor(tutor);
    setIsRatingModalOpen(true);
  };

  const openContactModal = (tutorName: string, isSuccess: boolean, message?: string) => {
    setContactModalData({
      isOpen: true,
      tutorName,
      isSuccess,
      message
    });
  };

  const openContactInfoModal = (tutorUserId: string) => {
    setContactInfoModalData({ isOpen: true, tutorUserId });
  };

  // Affichage de chargement
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#002c46] mx-auto mb-4"></div>
          <p className="text-gray-600 mb-2">Chargement des tuteur-trices...</p>
          <p className="text-sm text-[#002c46]">{connectionStatus}</p>
        </div>
      </div>
    );
  }

  // Affichage d'erreur avec option de fallback
  if (error && tutors.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-[#002c46] text-6xl mb-4">
            <i className="fas fa-shield-alt"></i>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Accès réservé aux étudiants</h2>
          <p className="text-gray-600 mb-4">
            Afin de respecter la loi sur la protection des données, vous devez être connecté avec votre compte étudiant pour accéder à la base de données de tuteurs.
          </p>
          <a
            href="/login"
            className="inline-block bg-heg-blue text-white px-6 py-2 rounded-lg hover:bg-heg-blue-dark transition-colors"
          >
            Se connecter
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Navigation */}
      <div className="container mx-auto px-6 pt-4">
        <BackButton to="/" label="Retour à l'accueil" />
        <Breadcrumbs />
      </div>

      {/* Indicateur de filtres avancés actifs */}
      {advancedFilters && (
        <div className="bg-blue-50 border-b border-blue-200 py-4">
          <div className="container mx-auto px-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <i className="fas fa-filter text-blue-600"></i>
                <div>
                  <h3 className="font-semibold text-blue-900">Filtres avancés actifs</h3>
                  <p className="text-sm text-blue-700">
                    {Object.entries(advancedFilters).filter(([key, value]) => 
                      value !== undefined && value !== null && value !== '' && 
                      !(typeof value === 'object' && Object.keys(value).length === 0)
                    ).map(([key, value]) => {
                      if (key === 'disponibilites' && typeof value === 'object') {
                        const dispos = Object.entries(value as any).map(([jour, creneaux]) => 
                          `${jour} (${(creneaux as string[]).join(', ')})`
                        ).join(' • ');
                        return `Disponibilités: ${dispos}`;
                      }
                      return `${key}: ${value}`;
                    }).join(' • ')}
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  navigate('/tutors', { replace: true });
                  window.location.reload();
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
              >
                <i className="fas fa-times mr-2"></i>
                Réinitialiser
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Section Filtre */}
      <section className="py-6 bg-gray-50">
        <div className="container mx-auto px-6">
          {/* Légende des créneaux au-dessus du filtre */}
          <div className="py-2">
            <AvailabilityLegend className="mb-4" />
          </div>
          <div className="flex flex-col gap-4 items-center max-w-md mx-auto">

            
            {/* Filtre dynamique par filière (masqué si filtres avancés actifs) */}
            {!advancedFilters && (
              <div className="w-full">
                {/* Indication discrète du filtre */}
                <div className="flex items-center justify-center mb-2">
                  <span className="text-xs text-gray-500 font-medium flex items-center gap-1.5">
                    <i className="fas fa-filter text-gray-400 text-[10px]"></i>
                    Filtrer par filière
                  </span>
                </div>
                <div className="rounded-2xl p-2.5 w-full bg-white/60 backdrop-blur-md border border-white/40 shadow-[0_8px_30px_rgba(0,0,0,0.06)]">
              {/* Sélecteur responsive: chips horizontales sur mobile, molette compacte sur desktop */}
              <div className="w-full">
                {/* Mobile: barre de chips scrollable, ultra compacte (4 boutons, re-cliquer désélectionne) */}
                <div className="md:hidden overflow-x-auto -mx-4 px-4">
                  <div className="flex items-center justify-center gap-2">
                    {[
                      { key: 'IS', label: 'IS', emoji: '📚', activeColor: 'bg-rose-200' },
                      { key: 'IG', label: 'IG', emoji: '💻', activeColor: 'bg-green-200' },
                      { key: 'EE', label: 'EE', emoji: '📊', activeColor: 'bg-blue-200' },
                      { key: 'IBM', label: 'IBM', emoji: '🌍', activeColor: 'bg-purple-200' }
                    ].map(({ key, label, emoji, activeColor }) => {
                      const filiereData = filterOptions.filieres.find(f => f.filiere.includes(key));
                      const count = filiereData?.count || 0;
                      const isSelected = selectedFiliere === key;
                      return (
                        <button
                          key={`chip-${key}`}
                          onClick={() => { setSelectedFiliere(selectedFiliere === key ? 'tous' : key); }}
                          disabled={optionsLoading}
                          className={`h-9 px-3 rounded-full whitespace-nowrap text-xs flex items-center gap-1 transition-colors ${
                            isSelected
                              ? `${activeColor} text-gray-800 shadow-sm`
                              : 'bg-white/80 text-gray-800 border border-white/60 hover:bg-white'
                          }`}
                          aria-pressed={isSelected}
                          aria-label={`Filtrer par filière ${label}`}
                        >
                          <span className="text-base leading-none">{emoji}</span>
                          <span className="font-semibold">{key}</span>
                          <span className="opacity-60 text-[10px]">({count})</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Desktop: molette compacte (4 boutons, re-cliquer désélectionne) */}
                <div className="hidden md:block bg-white/70 backdrop-blur rounded-xl p-2 border border-white/40">
                  <div className="grid grid-cols-4 gap-2 mb-2">
                    {[
                      { key: 'IS', label: 'IS', emoji: '📚', activeColor: 'bg-rose-200' },
                      { key: 'IG', label: 'IG', emoji: '💻', activeColor: 'bg-green-200' },
                      { key: 'EE', label: 'EE', emoji: '📊', activeColor: 'bg-blue-200' },
                      { key: 'IBM', label: 'IBM', emoji: '🌍', activeColor: 'bg-purple-200' }
                    ].map(({ key, label, emoji, activeColor }) => {
                      const filiereData = filterOptions.filieres.find(f => 
                        f.filiere.includes(key)
                      );
                      const count = filiereData?.count || 0;
                      
                      const isSelected = selectedFiliere === key;
                      
                      return (
                        <button
                          key={key}
                          onClick={() => { setSelectedFiliere(selectedFiliere === key ? 'tous' : key); }}
                          disabled={optionsLoading}
                          className={`flex flex-col items-center justify-center p-3 rounded-xl transition-all duration-150 ${
                            isSelected
                              ? `${activeColor} text-gray-800 shadow`
                              : 'bg-white/80 text-gray-800 border border-white/60 hover:bg-white'
                          } disabled:opacity-50`}
                        >
                          <div className="text-xl mb-0.5">{emoji}</div>
                          <div className={`font-semibold text-xs ${isSelected ? 'text-gray-800' : 'text-gray-800'}`}>
                            {key}
                          </div>
                          <div className={`text-[10px] ${isSelected ? 'text-gray-600' : 'text-gray-500'}`}>
                            {count}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Tutors Section */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-6">
          {filteredTutors.length > 0 ? (
            <>
              <div className="max-w-lg mx-auto mb-0">
                <Swiper
                  effect={'cards'}
                  grabCursor={true}
                  modules={[EffectCards]}
                  autoplay={false}
                  loop={false}
                  className="mySwiper"
                  style={{ height: '40rem' }}
                >
                  {displayTutors.map((tutor: Profile) => (
                    <SwiperSlide key={tutor.id}>
                      <TutorCard 
                        tutor={tutor} 
                        onRate={openRatingModal} 
                        onContactRequest={openContactModal}
                        onContactInfo={openContactInfoModal}
                      />
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>
            </>
          ) : (
            <div className="text-center py-16">
              <div className="text-gray-400 text-6xl mb-6">
                <i className="fas fa-user-graduate"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                {selectedFiliere === 'tous' 
                  ? 'Aucun-e tuteur-trice disponible'
                  : `Aucun tuteur disponible en ${selectedFiliere}`
                }
              </h3>
              <p className="text-gray-600 mb-6">
                {selectedFiliere === 'tous' 
                  ? 'Il n\'y a actuellement aucun tuteur disponible. Revenez plus tard ou devenez le premier tuteur !'
                  : `Il n'y a actuellement aucun tuteur disponible en ${selectedFiliere}. Essayez une autre filière ou devenez le premier tuteur de cette filière !`
                }
              </p>
              {selectedFiliere !== 'tous' && (
                <button
                  onClick={() => setSelectedFiliere('tous')}
                  className="mt-4 px-6 py-3 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors"
                >
                  <i className="fas fa-arrow-left mr-2"></i>
                  Voir tous les tuteurs
                </button>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Section CTA Recherche Avancée */}
      <section className="py-8 bg-white border-t">
        <div className="container mx-auto px-6 text-center">
          <p className="text-gray-600 mb-4">
            Besoin de critères plus précis ?
          </p>
          <button
            onClick={() => navigate('/filter')}
            className="px-6 py-3 bg-gray-800 text-white rounded-full font-semibold hover:bg-gray-900 transition-colors"
          >
            <i className="fas fa-sliders-h mr-2"></i>
            Recherche avancée
          </button>
        </div>
      </section>


      {/* Modal de notation */}
      {isRatingModalOpen && selectedTutor && (
        <RatingModal
          tutorId={selectedTutor.id}
          isOpen={isRatingModalOpen}
          onClose={() => {
            setIsRatingModalOpen(false);
            setSelectedTutor(null);
          }}
          onSuccess={handleRatingSuccess}
          showToast={showToast}
        />
      )}

      {/* Modal de demande de contact */}
      <ContactRequestModal
        isOpen={contactModalData.isOpen}
        onClose={() => setContactModalData({ ...contactModalData, isOpen: false })}
        tutorName={contactModalData.tutorName}
        isSuccess={contactModalData.isSuccess}
        message={contactModalData.message}
      />

      {/* Modal d'informations de contact */}
      <ContactInfoModal
        isOpen={contactInfoModalData.isOpen}
        onClose={() => setContactInfoModalData({ ...contactInfoModalData, isOpen: false })}
        matchedUserId={contactInfoModalData.tutorUserId}
        isStudent={false}
        onRateTutor={() => {
          const tutor = tutors.find(t => t.user_id === contactInfoModalData.tutorUserId);
          if (tutor) {
            setSelectedTutor(tutor);
            setIsRatingModalOpen(true);
            setContactInfoModalData({ ...contactInfoModalData, isOpen: false });
          }
        }}
      />
    </div>
  );
};